import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Paperclip, Trash } from "@phosphor-icons/react";
import { openAuthFile } from "../../lib/fileOpen";

const EMPTY = { user_id: "", qualification: "", category: "", reference_number: "", issued_date: "", expiry_date: "", issuing_body: "", evidence_url: "", evidence_name: "", notes: "" };

function addMonths(iso, months) {
  const d = new Date(iso + "T00:00:00");
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** Add / edit one operative qualification (card, ticket, certificate) with optional evidence upload. */
export default function QualificationDialog({ open, onOpenChange, qual, operatives, types, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const editing = !!qual?.id;

  useEffect(() => { if (open) setF({ ...EMPTY, ...(qual || {}) }); }, [open, qual]);
  const set = (patch) => setF(x => ({ ...x, ...patch }));

  const pickQual = (name) => {
    const t = types.find(x => x.name === name);
    const patch = { qualification: name };
    if (t) { patch.category = t.category; if (f.issued_date && !f.expiry_date && t.typical_validity_months) patch.expiry_date = addMonths(f.issued_date, t.typical_validity_months); }
    set(patch);
  };
  const setIssued = (issued_date) => {
    const t = types.find(x => x.name === f.qualification);
    set({ issued_date, ...(issued_date && t?.typical_validity_months && !f.expiry_date ? { expiry_date: addMonths(issued_date, t.typical_validity_months) } : {}) });
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await API.post(`/upload?entity_type=qualification&entity_id=${f.user_id || "new"}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ evidence_url: data.file_url || `/api/files/${data.file_id}/${data.file_name}`, evidence_name: file.name });
    } catch { toast.error("Upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.user_id) return toast.error("Choose an operative");
    if (!f.qualification.trim()) return toast.error("Qualification name is required");
    setBusy(true);
    try {
      const { data } = editing ? await API.put(`/hs/qualifications/${qual.id}`, f) : await API.post("/hs/qualifications", f);
      toast.success(editing ? "Qualification updated" : "Qualification added");
      onSaved(data); onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${qual.qualification} from ${qual.operative_name}?`)) return;
    try { await API.delete(`/hs/qualifications/${qual.id}`); toast.success("Removed"); onSaved(null); onOpenChange(false); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="qualification-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{editing ? "Edit qualification" : "Add qualification"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[11px]">Operative</Label>
            <select value={f.user_id} onChange={e => set({ user_id: e.target.value })} disabled={editing} className="mt-1 h-9 w-full rounded-md border border-border bg-background text-sm px-2" data-testid="qual-operative">
              <option value="">Choose…</option>
              {operatives.map(o => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <Label className="text-[11px]">Qualification / card</Label>
            <Input list="qual-types" value={f.qualification} onChange={e => pickQual(e.target.value)} placeholder="Start typing — e.g. Gas Safe, 18th Edition, IPAF" className="mt-1 h-9 text-sm" data-testid="qual-name" />
            <datalist id="qual-types">{types.map(t => <option key={t.name} value={t.name} />)}</datalist>
          </div>
          <div><Label className="text-[11px]">Category</Label><Input value={f.category} onChange={e => set({ category: e.target.value })} className="mt-1 h-9 text-sm" data-testid="qual-category" /></div>
          <div><Label className="text-[11px]">Card / certificate no.</Label><Input value={f.reference_number} onChange={e => set({ reference_number: e.target.value })} className="mt-1 h-9 text-sm" data-testid="qual-ref" /></div>
          <div><Label className="text-[11px]">Issued</Label><Input type="date" value={f.issued_date} onChange={e => setIssued(e.target.value)} className="mt-1 h-9 text-sm" data-testid="qual-issued" /></div>
          <div><Label className="text-[11px]">Expires <span className="text-muted-foreground">(blank = no expiry)</span></Label><Input type="date" value={f.expiry_date} onChange={e => set({ expiry_date: e.target.value })} className="mt-1 h-9 text-sm" data-testid="qual-expiry" /></div>
          <div className="col-span-2"><Label className="text-[11px]">Issuing body</Label><Input value={f.issuing_body} onChange={e => set({ issuing_body: e.target.value })} placeholder="e.g. Gas Safe Register, City & Guilds, IPAF" className="mt-1 h-9 text-sm" data-testid="qual-issuer" /></div>
          <div className="col-span-2">
            <Label className="text-[11px]">Evidence (scan / photo of the card)</Label>
            <div className="mt-1 flex items-center gap-2">
              {f.evidence_url ? (
                <>
                  <button type="button" onClick={() => openAuthFile(f.evidence_url)} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="qual-evidence-link"><Paperclip size={12} /> {f.evidence_name || "Evidence"}</button>
                  <button type="button" onClick={() => set({ evidence_url: "", evidence_name: "" })} className="text-xs text-red-600 hover:underline">Remove</button>
                </>
              ) : (
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="qual-evidence-upload"><Paperclip size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload file"}</Button>
              )}
              <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
            </div>
          </div>
          <div className="col-span-2"><Label className="text-[11px]">Notes</Label><Textarea value={f.notes} onChange={e => set({ notes: e.target.value })} rows={2} className="mt-1 text-xs" data-testid="qual-notes" /></div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="ghost" size="sm" className="mr-auto text-red-600" onClick={remove} data-testid="qual-delete"><Trash size={14} className="mr-1" /> Remove</Button>}
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="qual-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
